<script setup lang="ts">
import { ref } from 'vue'
import axios from 'axios'

interface EmailList {
  key: string
  emails: string[]
}

const props = defineProps<{
  initialLists: EmailList[]
}>()

const lists = ref<EmailList[]>(
  props.initialLists.map(l => ({ key: l.key, emails: [...l.emails] }))
)

const newEmails = ref<Record<string, string>>(
  Object.fromEntries(props.initialLists.map(l => [l.key, '']))
)
const savingKey = ref<string | null>(null)
const savedKey = ref<string | null>(null)

const onAddEmail = (list: EmailList): void => {
  const val = (newEmails.value[list.key] || '').trim()
  if (!val || isValidEmail(val) === false) return
  list.emails.push(val)
  newEmails.value[list.key] = ''
  onSave(list)
}

const onDeleteEmail = (list: EmailList, index: number): void => {
  list.emails.splice(index, 1)
  onSave(list)
}

const isValidEmail = (val: string | undefined): boolean | null => {
  if (val === undefined || val === null) return null
  const v = val.trim()
  if (v === '') return null  // neutral state for empty
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || false
}

const onSave = async (list: EmailList): Promise<void> => {
  if (savingKey.value === list.key) return
  // Don't save if any visible email is invalid
  if (list.emails.some(e => e.trim() !== '' && isValidEmail(e) === false)) return
  savingKey.value = list.key
  try {
    const nonEmpty = list.emails.filter(e => e.trim() !== '')
    const resp = await axios.post('/admin/email-notifications', {
      key: list.key,
      emails: nonEmpty,
    })
    list.emails = [...resp.data.emails]
    savedKey.value = list.key
    setTimeout(() => { if (savedKey.value === list.key) savedKey.value = null }, 2000)
  } finally {
    savingKey.value = null
  }
}
</script>

<template>
  <div>
    <b-row class="mb-3">
      <b-col>
        <p class="text-muted">Configure email recipients for each notification event type.</p>
      </b-col>
    </b-row>

    <div
      v-for="list in lists"
      :key="list.key"
      class="list-row mb-5"
    >
      <b-row>
        <b-col md="4">
          <h6 class="text-right list-key">
            <code>{{ list.key }}</code>
          </h6>
          <p v-if="savedKey === list.key" class="text-right text-success small mb-0">✔ Saved</p>
        </b-col>
        <b-col md="8">
          <!-- New email input -->
          <div class="email-row mb-1">
            <b-input-group size="sm">
              <b-form-input
                v-model="newEmails[list.key]"
                type="email"
                placeholder="Add email..."
                :state="isValidEmail(newEmails[list.key])"
                @keydown.enter.prevent="onAddEmail(list)"
              />
            </b-input-group>
          </div>

          <!-- Existing emails -->
          <div
            v-for="(email, idx) in list.emails"
            :key="idx"
            class="email-row mb-1"
          >
            <b-input-group size="sm">
              <b-form-input
                v-model="list.emails[idx]"
                type="email"
                placeholder="email@example.com"
                :state="isValidEmail(list.emails[idx])"
                @keydown.enter.prevent="onSave(list)"
                @blur="onSave(list)"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-danger"
                  @click="onDeleteEmail(list, idx)"
                  title="Remove email"
                >
                  ✕
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>

        </b-col>
      </b-row>
    </div>

    <p class="text-muted small mt-5">Last updated: {{ new Date().toISOString() }}</p>
  </div>
</template>

<style scoped>
.list-key {
  margin-top: 4px;
  word-break: break-word;
}
code {
  color: #6b3030;
  font-size: 90%;
}
</style>
